import React, { useMemo, useState } from "react";
import { GrowthReport } from "./types";
import { fmtDuration, fmtN, screenLabel } from "./format";
import { Fx } from "./Sheet";
import { MatrixCard } from "./MatrixCard";

type LogFilter = "all" | "dj" | "fan" | "errors";

// The "Details" sheet — everything that is not the drop-off question: funnel
// by segment, time to reach each step, comebacks, speed to first value,
// signup currencies, friction & errors, the raw event feed. "Where people leave" has its own
// sheet (ExitsSheet.tsx).
export function GrowthMoreDetail({ report }: { report: GrowthReport }) {
  const [logFilter, setLogFilter] = useState<LogFilter>("all");

  const filteredLog = useMemo(() => {
    if (!report) return [];
    return report.recent.filter((e) => {
      if (logFilter === "dj") return e.selectedPath === "dj";
      if (logFilter === "fan") return e.selectedPath === "fan";
      if (logFilter === "errors") return e.name === "signup_failed" || e.name === "invite_code_failed" || !!e.errorReason;
      return true;
    });
  }, [report, logFilter]);

  const currencies = report.breakdowns.currency.map((r) => `${r.key.toUpperCase()} ${fmtN(r.accounts)}`).join(" · ");

  const times = report.funnels.all.dj.filter((st) => report.stepTimes.dj[st.key] != null);

  return (
    <>
      <Fx>Funnel by segment · Time to reach each step · Comebacks · Speed to first value · Signup currencies · Friction &amp; errors · Recent events</Fx>
      <MatrixCard matrix={report.matrix} steps={report.matrixSteps} />
      <div className="gr-two-wide">
        <div className="adm-card">
          <h3>
            Time to reach each step <small>· median for talent who signed up in this window, from the step before · all platforms</small>
          </h3>
          {times.length === 0 ? (
            <div className="gr-muted gr-pad-top">No timed steps in this window yet.</div>
          ) : (
            <table className="gr-table gr-pad-top">
              <tbody>
                {times.map((st) => (
                  <tr key={st.key}>
                    <td className="gr-key">{st.label}</td>
                    <td className="gr-num">{fmtDuration(report.stepTimes.dj[st.key])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="gr-stack-col">
          <div className="adm-card">
            <h3>
              Comebacks <small>· people who left, then returned</small>
            </h3>
            <div className="gr-duo">
              <div className="gr-cell">
                <div className="gr-cell-lab">Devices seen in 2+ visits</div>
                <div className="gr-cell-val">{report.comebacks.returnedPct != null ? `${report.comebacks.returnedPct}%` : "—"}</div>
              </div>
              <div className="gr-cell">
                <div className="gr-cell-lab">Abandoned signups that returned → completed</div>
                <div className="gr-cell-val">
                  {fmtN(report.comebacks.abandonedCompleted)}
                  {report.comebacks.abandoned > 0 && <small> · {fmtN(report.comebacks.abandoned)} still out</small>}
                </div>
              </div>
            </div>
            <div className="gr-note">Counted per device — a return on a different device looks like a new visitor.</div>
          </div>
          <div className="adm-card">
            <h3>
              Speed to first value <small>· median</small>
            </h3>
            <div className="gr-duo">
              <div className="gr-cell">
                <div className="gr-cell-lab">Talent: signup → bank connected</div>
                <div className="gr-cell-val">{fmtDuration(report.speed.djActivateMedianMs)}</div>
              </div>
              <div className="gr-cell">
                <div className="gr-cell-lab">Fan: first visit → first request paid</div>
                <div className="gr-cell-val">{fmtDuration(report.speed.fanFirstPaidMedianMs)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="gr-currencies">
        <b>Signup currencies:</b> {currencies || "none in this window yet"}
      </div>

      <div className="gr-two-wide">
        <div className="adm-card">
          <h3>
            Friction &amp; errors <small>· ranked by people affected</small>
          </h3>
          <div className="gr-pad-top">
            <div className="gr-fr-row">
              <span className="gr-fr-rank">1</span>
              <span className="gr-fr-main">
                <span className="gr-fr-t">Exited at the invite-code step</span>
                <span className="gr-fr-s">
                  {fmtN(report.friction.inviteExits.neverSubmitted)} never submitted a code ·{" "}
                  {fmtN(report.friction.inviteExits.afterFailure)} left after a failed try
                </span>
              </span>
              <span className="gr-fr-n">{fmtN(report.friction.inviteExits.total)}</span>
            </div>
            <div className="gr-fr-row">
              <span className="gr-fr-rank">2</span>
              <span className="gr-fr-main">
                <span className="gr-fr-t">Invalid invite codes entered</span>
                <span className="gr-fr-s">
                  {fmtN(report.friction.invalidCodes.entries)} entries by {fmtN(report.friction.invalidCodes.people)} people
                </span>
              </span>
              <span className="gr-fr-n">{fmtN(report.friction.invalidCodes.people)}</span>
            </div>
            <div className="gr-fr-row">
              <span className="gr-fr-rank">3</span>
              <span className="gr-fr-main">
                <span className="gr-fr-t">Sign-ups that stopped mid-way — last step reached</span>
                <span className="gr-fr-s">
                  {report.friction.signupStops.length === 0
                    ? "none in this window"
                    : report.friction.signupStops.map((s) => `${screenLabel(s.step)} ${s.n}`).join(" · ")}
                </span>
              </span>
              <span className="gr-fr-n">{fmtN(report.friction.signupStops.reduce((a, s) => a + s.n, 0))}</span>
            </div>
            <div className="gr-fr-row">
              <span className="gr-fr-rank">4</span>
              <span className="gr-fr-main">
                <span className="gr-fr-t">Signup failures</span>
                <span className="gr-fr-s">
                  {report.friction.signupFailures.length === 0
                    ? "none in this window"
                    : report.friction.signupFailures.map((f) => `${f.reason} ${f.n}`).join(" · ")}
                </span>
              </span>
              <span className="gr-fr-n gr-err">{fmtN(report.friction.signupFailures.reduce((a, f) => a + f.n, 0))}</span>
            </div>
          </div>
        </div>

        <div className="adm-card gr-log">
          <h3>
            Recent events <small>· raw feed for debugging — the sheets are the source of truth</small>
          </h3>
          <div className="gr-chips gr-chips-tight">
            {(["all", "dj", "fan", "errors"] as LogFilter[]).map((f) => (
              <button key={f} className={logFilter === f ? "on" : ""} onClick={() => setLogFilter(f)}>
                {f === "all" ? "All" : f === "dj" ? "Talent" : f === "fan" ? "Fan" : "Errors"}
              </button>
            ))}
          </div>
          {filteredLog.length === 0 ? (
            <div className="gr-muted">Nothing here yet.</div>
          ) : (
            filteredLog.slice(0, 20).map((e) => (
              <div className="gr-log-row" key={e._id}>
                {new Date(e.createdAt).toLocaleTimeString()}{" "}
                <span className={`gr-log-ev${e.name === "signup_failed" || e.name === "invite_code_failed" ? " gr-err" : ""}`}>
                  {e.name}
                </span>{" "}
                {e.selectedPath && (
                  <span className={`gr-pathchip ${e.selectedPath === "dj" ? "gr-pc-dj" : "gr-pc-fan"}`}>
                    {e.selectedPath.toUpperCase()}
                  </span>
                )}{" "}
                {[e.platform, e.appVersion, e.country, e.anonymousId ? `${e.anonymousId.slice(0, 8)}…` : null, e.errorReason]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}
